import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, ArrowLeft, ArrowRight } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/brand";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const INDUSTRIES = ["Construction", "Facilities Management", "Property Maintenance", "Property Management", "Manufacturing", "Warehousing", "Logistics", "Transport", "Retail", "Hospitality", "Healthcare", "Education", "Cleaning", "Agriculture", "Utilities", "Engineering", "Automotive", "Landscaping", "Security", "Offices", "Professional Services", "Public Sector", "Charities", "General SME"];
const SIZES = ["Sole trader", "2–9", "10–49", "50–249", "250–999", "1000+"];
const ACTIVITIES = ["Working at height", "Electrical work", "Plumbing & heating", "Gas work", "Hot works", "Confined spaces", "Lifting operations", "Driving for work", "Lone working", "Hazardous substances", "Manual handling", "Machinery & plant", "Office / DSE work", "Work in occupied buildings", "Demolition / refurbishment", "Excavation"];
const NEEDS = ["RAMS", "Risk assessments", "Method statements", "COSHH", "CDM documents", "Toolbox talks", "HSE policies", "Fire safety", "Permits to work", "Inspections", "Incident reporting", "Training records", "Environmental management"];

const Chips = ({ options, value, onChange, testid, single }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((o) => {
      const on = single ? value === o : value.includes(o);
      return (
        <button type="button" key={o} data-testid={`${testid}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => onChange(single ? o : on ? value.filter((x) => x !== o) : [...value, o])}
          className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm transition-colors ${on ? "border-health bg-health/10 text-foreground" : "hover:bg-accent"}`}>
          {on && <Check className="h-3.5 w-3.5 text-health" />}{o}
        </button>
      );
    })}
  </div>
);

export default function Onboarding() {
  const nav = useNavigate();
  const { refresh, user } = useAuth();
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ registered_name: "", trading_name: "", company_number: "", vat_number: "", business_address: "", telephone: "", contact_email: user?.email || "", website: "", primary_hse_contact: user?.name || "", industries: [], org_size: "", activities: [], hse_needs: [], plan_slug: params.get("plan") || "team" });
  const set = (k) => (v) => setF({ ...f, [k]: v?.target ? v.target.value : v });
  const steps = [
    ["Company details", "We'll reuse these in every document so you never retype them.", (
      <div className="grid gap-4 sm:grid-cols-2">
        {[["registered_name", "Registered company name *"], ["trading_name", "Trading name"], ["company_number", "Company number"], ["vat_number", "VAT number"], ["business_address", "Business address"], ["telephone", "Telephone"], ["contact_email", "Contact email"], ["website", "Website"], ["primary_hse_contact", "Primary HSE contact"]].map(([k, l]) => (
          <div key={k} className={k === "business_address" ? "sm:col-span-2 space-y-1.5" : "space-y-1.5"}><Label>{l}</Label><Input data-testid={`onboarding-${k}`} value={f[k]} onChange={set(k)} /></div>
        ))}
      </div>
    )],
    ["Your industry", "Choose every sector you operate in.", <Chips options={INDUSTRIES} value={f.industries} onChange={set("industries")} testid="industry" />],
    ["Size & work activities", "This helps Saflr suggest relevant documents and controls.", (
      <div className="space-y-6"><div><Label className="mb-2 block">Organisation size</Label><Chips single options={SIZES} value={f.org_size} onChange={set("org_size")} testid="size" /></div>
        <div><Label className="mb-2 block">Main work activities</Label><Chips options={ACTIVITIES} value={f.activities} onChange={set("activities")} testid="activity" /></div></div>
    )],
    ["HSE needs", "What do you need Saflr to help with first?", <Chips options={NEEDS} value={f.hse_needs} onChange={set("hse_needs")} testid="need" />],
  ];
  const finish = async () => {
    setBusy(true);
    try {
      await api.post("/onboarding", f);
      await refresh();
      toast.success("Your workspace is ready");
      nav("/app");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  const [title, sub, body] = steps[step];
  const next = () => {
    if (step === 0 && !f.registered_name.trim()) return toast.error("Registered company name is required");
    step === steps.length - 1 ? finish() : setStep(step + 1);
  };
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b"><div className="mx-auto flex h-16 max-w-4xl items-center justify-between px-5"><Logo to="/onboarding" /><span className="font-mono text-xs text-muted-foreground">Step {step + 1} of {steps.length}</span></div></header>
      <main className="mx-auto max-w-4xl px-5 py-12">
        <div className="mb-10 flex gap-2">{steps.map((_, i) => <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? "bg-health" : "bg-muted"}`} />)}</div>
        <div key={step} className="fade-up">
          <h1 className="text-3xl font-bold sm:text-4xl">{title}</h1>
          <p className="mt-2 text-muted-foreground">{sub}</p>
          <div className="mt-8 rounded-2xl border bg-card p-6 sm:p-8">{body}</div>
        </div>
        <div className="mt-8 flex justify-between">
          <Button variant="ghost" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="onboarding-back"><ArrowLeft className="mr-1 h-4 w-4" />Back</Button>
          <Button onClick={next} disabled={busy} className="rounded-full px-6" data-testid="onboarding-next">{step === steps.length - 1 ? (busy ? "Creating workspace…" : "Start 14-day trial") : "Continue"}<ArrowRight className="ml-1 h-4 w-4" /></Button>
        </div>
      </main>
    </div>
  );
}
